import React, { useMemo } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip,
} from '@mui/material';
import { Include, IncludeService } from 'us-web-services';
import { Table, TableInstance } from 'src/components/common/Table';
import PageStyles from '../../styles/PageStyles';
import DateUtil from '../../util/DateUtil';

function IncludesLookup(props: RouteComponentProps) {
  const { history } = props;
  const [showConfirmDisable, setShowConfirmDisable] = React.useState(false);
  const [showConfirmEnable, setShowConfirmEnable] = React.useState(false);
  const [currentRow, setCurrentRow] = React.useState<Include>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const tableRef = React.useRef<TableInstance>();

  const { classes } = PageStyles();

  const params = (globalFilter: string) => {
    const paramArray = [];

    if (globalFilter) {
      const encodedFilter = globalFilter.replace("'", "\\'");

      if (!+globalFilter) {
        let q = `label.contains('${encodedFilter}')`;

        q += `.or(type.contains('${encodedFilter}'))`;

        paramArray.push({
          key: 'q',
          value: q,
        });
      } else {
        paramArray.push({
          key: 'id',
          value: globalFilter,
        });
      }
    }

    return paramArray;
  };

  const closeConfirmDisableDialog = () => {
    setShowConfirmDisable(false);
    setCurrentRow(undefined);
  };

  const closeConfirmEnableDialog = () => {
    setShowConfirmEnable(false);
    setCurrentRow(undefined);
  };

  const refreshData = () => tableRef.current?.fetchData();

  const disableItem = async () => {
    if (!currentRow?.id) return;

    setIsSubmitting(true);

    const patchData = {
      id: currentRow.id,
      label: currentRow.label,
      type: currentRow.type,
      disabled: DateUtil.getCurrentDateTimeString(),
    } as Include;

    try {
      await IncludeService.patch(currentRow.id, patchData);
      closeConfirmDisableDialog();
      setIsSubmitting(false);
      refreshData();
      toast.success('Include disabled successfully');
    } catch {
      setIsSubmitting(false);
      toast.error('Error disabling include');
    }
  };

  const enableItem = async () => {
    if (!currentRow?.id) return;

    setIsSubmitting(true);

    const patchData = {
      id: currentRow.id,
      label: currentRow.label,
      type: currentRow.type,
      disabled: null,
    } as Include;

    try {
      await IncludeService.patch(currentRow.id, patchData);
      closeConfirmEnableDialog();
      setIsSubmitting(false);
      refreshData();
      toast.success('Include enabled successfully');
    } catch {
      setIsSubmitting(false);
      toast.error('Error enabling include');
    }
  };

  const editItem = async (item) => {
    if (item.values.label === item.row.original.label && item.values.type === item.row.original.type) {
      return;
    }

    const data = {
      id: item.row.original.id,
      label: item.values.label,
      type: item.values.type,
      disabled: item.row.original.disabled,
    } as Include;

    try {
      await IncludeService.patch(item.row.original.id, data);
      refreshData();
      toast.success('Include updated successfully');
    } catch {
      toast.error('Error updating include');
      refreshData();
    }
  };

  const confirmDisable = (rowData) => {
    setShowConfirmDisable(true);
    setCurrentRow(rowData);
  };

  const confirmEnable = (rowData) => {
    setShowConfirmEnable(true);
    setCurrentRow(rowData);
  };

  const addItem = () => {
    history.push('/includes/add');
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: 'includes',
        header: 'Includes',
        columns: [
          {
            header: 'Status',
            enableEditing: false,
            size: 100,
            accessorFn: row => row?.disabled ? 'Disabled' : 'Enabled',
            Cell: ({ cell }) => (
              <>
                {!!cell?.row?.original.disabled &&
                  <Tooltip title='Enable include' placement='top-start'>
                    <Button
                      size='small'
                      variant='contained'
                      color='error'
                      onClick={() => confirmEnable(cell?.row?.original)}
                    >
                      Disabled
                    </Button>
                  </Tooltip>
                }
                {!cell?.row?.original.disabled &&
                  <Tooltip title='Disable include' placement='top-start'>
                    <Button
                      size='small'
                      variant='contained'
                      color='success'
                      onClick={() => confirmDisable(cell?.row?.original)}
                    >
                      Enabled
                    </Button>
                  </Tooltip>
                }
              </>
            ),
          },
          {
            accessorKey: 'id',
            header: 'ID',
            enableEditing: false,
            size: 60,
          },
          {
            accessorKey: 'type',
            header: 'Type',
          },
          {
            accessorKey: 'label',
            header: 'Label',
          },
        ],
      },
    ],
    [],
  );

  return (
    <Paper className={classes.paper}>
      <Dialog open={showConfirmDisable} onClose={closeConfirmDisableDialog}>
        <DialogTitle>Confirm Include Deactivation</DialogTitle>
        <DialogContent>
          Are you sure you want to disable this include?
          It will no longer appear in the product but will be kept for record purposes.
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDisableDialog} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={disableItem}
            disabled={isSubmitting}
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showConfirmEnable} onClose={closeConfirmEnableDialog}>
        <DialogTitle>Confirm Include Activation</DialogTitle>
        <DialogContent>
          Are you sure you want to enable this include?
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmEnableDialog} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={enableItem}
            disabled={isSubmitting}
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Table
        ref={tableRef}
        columns={COLUMNS}
        retrieveData={IncludeService.getByFilterForAdmin}
        params={params}
        addAction={addItem}
        editAction={editItem}
        enableInlineEditing
        alwaysApplyParams
      />
    </Paper>
  );
}

export default withRouter(IncludesLookup);
